/**
 * The one config-bound module: builds `field key -> SearchFilterType` for a
 * scope from `filterConfigs`. Kept separate from the pure pipeline so importing
 * the engine's logic doesn't drag the whole filter/UI import graph (see
 * pipeline.ts / codec-selection.ts).
 */
import { SearchTable } from '@aibees/elephant/types';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import { filterConfigs } from '../config';
import type { SearchFilter } from '../types';
import type { FieldTypeIndex } from './pipeline';

const SCOPE_TO_TABLE: Record<SearchTableScope, SearchTable> = {
  company: SearchTable.Company,
  people: SearchTable.People,
  employees: SearchTable.Employees,
};

const indexCache = new Map<SearchTableScope, Map<string, string>>();

function collect(
  items: SearchFilter[] | undefined,
  parentType: string,
  out: Map<string, string>
): void {
  if (!items) return;
  for (const item of items) {
    const type = (item.type as string) ?? parentType;
    if (item.field) out.set(item.field, type);
    collect(item.items, type, out);
    collect(item.advance, type, out);
  }
}

/** field key -> declared SearchFilterType (string) for a scope (memoized). */
export function buildFieldTypeIndex(scope: SearchTableScope): FieldTypeIndex {
  const cached = indexCache.get(scope);
  if (cached) return cached;
  const out = new Map<string, string>();
  for (const base of filterConfigs[SCOPE_TO_TABLE[scope]] ?? []) {
    collect(base.items, base.type as string, out);
  }
  indexCache.set(scope, out);
  return out;
}
